import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import * as yup from 'yup'

import {
  AddContentIcon,
  AddIcon,
  AddImageIcon,
  BulletedListIcon,
  EditIcon,
  NumberedListIcon,
  SaveIcon,
} from 'src/v3/core/icons'

import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectActiveSection,
  selectActiveCourse,
  selectActiveClass,
  fetchClasses,
  selectClassesByCourseID,
  selectClassesStatus,
  activeClassChanged,
} from 'src/v3/create/create.slice'

import theme from 'src/theme'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { useFormik } from 'formik'
import axios from 'src/utilities/http-client'
import { compose, F, T } from 'ramda'
import { processImageFile, uploadImageToS3 } from 'src/v3/utilities/s3'
import noop from 'src/utilities/noop'
import {
  SelectLinkedService,
  SelectSection,
  SelectCourse,
} from 'src/v3/create/components/select-entities'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'

let moveItem = (list: any[], oldIndex: number, newIndex: number) => {
  let without = list.filter((x, i) => i !== oldIndex)
  return [
    ...without.slice(0, newIndex),
    list[oldIndex],
    ...without.slice(newIndex),
  ]
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let activeSection = useAppSelector(selectActiveSection)
  let activeCourse = useAppSelector(selectActiveCourse)
  let activeClass = useAppSelector(selectActiveClass)

  let classes = useAppSelector(selectClassesByCourseID(activeCourse))
  let classesStatus = useAppSelector(selectClassesStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [uploading, setUploading] = useState<string>('')
  let [deleting, setDeleting] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [classOrder, setClassOrder] = useState<string[]>([])
  let [view, setView] = useState<'list' | 'order'>('list')

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      description: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      description: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/create/class', {
            name: values.name,
            description: values.description,
            courseID: activeCourse,
            sequence: classes.length,
          })
          .then(() => dispatch(fetchClasses()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Class')
            dispatch(fetchClasses())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/class', {
            id: values.id,
            name: values.name,
            description: values.description,
          })
          .then(() => dispatch(fetchClasses()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Class')
            dispatch(fetchClasses())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  useEffect(() => {
    if (classesStatus === 'fulfilled') {
      setClassOrder(classes.map((c) => c.id))
    }
  }, [activeCourse, classesStatus])

  let onEdit = (id: string) => {
    let classForEdit = classes.find((c) => c.id === id)
    form.setFieldValue('id', classForEdit?.id)
    form.setFieldValue('name', classForEdit?.name)
    form.setFieldValue('description', classForEdit?.description)
    setFormOpen(true)
  }

  let onImageUpload = (id: string, t: any) => {
    setUploading(id)
    return axios
      .put('/api/v2/create/class/image', { id })
      .then((response) => uploadImageToS3(t, response.data))
      .then(() => dispatch(fetchClasses()))
      .then(() => setUploading(''))
      .catch((err) => {
        setUploading('')
        setErrorMessage(err?.message || 'Error Uploading Image')
      })
  }

  return (
    <>
      <Box component="main" sx={{ p: 3 }}>
        <Typography
          variant="h4"
          className="my-4 d-inline"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Classes
        </Typography>
        <Typography
          className="d-inline ml-3"
          sx={{
            marginBottom: '16px',
          }}
        >
          Create a class or select one to edit
        </Typography>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            margin: '16px 0',
          }}
        >
          <SelectLinkedService />
          <SelectSection />
          <SelectCourse />
        </div>
        {view === 'list' ? (
          <div>
            <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <Box>
                <Paper
                  sx={{
                    p: 2,
                    margin: 'auto',
                    flexGrow: 1,
                  }}
                >
                  <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    <Box className="flex gap-4">
                      <Box className="flex-1 flex flex-col gap-4">
                        <Box className="flex-1">
                          <Typography
                            gutterBottom
                            variant="subtitle1"
                            component="div"
                          >
                            <Skeleton animation={false} />
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <Skeleton animation={false} />
                          </Typography>
                        </Box>
                        <Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexFlow: 'row-reverse wrap',
                              gap: 1,
                            }}
                          >
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              disabled={
                                activeSection === 'unselected' ||
                                activeCourse === 'unselected'
                              }
                              onClick={compose(setFormOpen, T)}
                            >
                              Add New Class
                            </Button>
                            {classes.length > 1 && (
                              <Button
                                variant="outlined"
                                startIcon={<NumberedListIcon />}
                                disabled={
                                  activeSection === 'unselected' ||
                                  activeCourse === 'unselected'
                                }
                                onClick={() => setView('order')}
                              >
                                Reorder Classes
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <Box>{/* Nothing here */}</Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
              {classes.map((c) => (
                <Box>
                  <Paper
                    sx={{
                      p: 2,
                      margin: 'auto',
                      flexGrow: 1,
                    }}
                    style={{
                      backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${c.imageName})`,
                      backgroundSize: 'cover',
                      backgroundPosition: '0px 48%',
                      boxShadow: 'inset 0 0 0 1000px rgba(37, 43, 66, 0.94)',
                    }}
                  >
                    <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      <Box className="flex gap-4">
                        <Box className="flex-1 flex flex-col gap-4">
                          <Box className="flex flex-row">
                            <Box className="flex-1">
                              <Typography
                                gutterBottom
                                variant="subtitle1"
                                component="div"
                              >
                                {c.name}
                              </Typography>
                              <Typography variant="body2" gutterBottom>
                                {c.description || 'N/A'}
                              </Typography>
                              <Typography
                                color="text.secondary"
                                variant="body2"
                                gutterBottom
                              >
                                Class {c.sequence + 1}
                              </Typography>
                            </Box>
                            <Box className="flex-1">
                              <Box
                                sx={{
                                  pl: 1,
                                  mt: 1,
                                  display: 'flex',
                                  flexDirection: 'row-reverse',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<AddContentIcon />}
                                  onClick={() => {
                                    dispatch(activeClassChanged(c.id))
                                    navigate('/create/title-screen')
                                  }}
                                >
                                  Manage Content
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                          <Box>
                            <Box
                              sx={{
                                pl: 1,
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                flexWrap: 'wrap',
                                gap: 1,
                                alignItems: 'center',
                              }}
                            >
                              <Tooltip title="Delete">
                                <Button
                                  variant="outlined"
                                  onClick={() => setDeleting(`delete-${c.id}`)}
                                >
                                  <DeleteForeverIcon />
                                </Button>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <Button
                                  variant="outlined"
                                  onClick={() => onEdit(c.id)}
                                >
                                  <EditIcon />
                                </Button>
                              </Tooltip>
                              {uploading !== c.id ? (
                                <Tooltip title="Upload Thumbnail">
                                  <Button variant="outlined" component="label">
                                    <AddImageIcon onClick={noop} />
                                    <input
                                      type="file"
                                      accept=".jpg,.jpeg,.png"
                                      hidden
                                      onChange={(event) =>
                                        processImageFile(event, {
                                          onLoadingStart: noop,
                                          onLoadingEnd: (t: any) => {
                                            onImageUpload(c.id, t)
                                          },
                                        })
                                      }
                                    />
                                  </Button>
                                </Tooltip>
                              ) : (
                                <CircularProgress size={28} />
                              )}
                            </Box>
                          </Box>
                          <ConfirmAction
                            executeAction={() =>
                              axios
                                .delete(
                                  `/api/v2/create/delete-class?classID=${c.id}`,
                                )
                                .then(() =>
                                  axios.put('/api/v2/create/class/sequence', {
                                    courseID: activeCourse,
                                    classes: classOrder.filter(
                                      (co) => co !== c.id,
                                    ),
                                  }),
                                )
                                .then(() => dispatch(fetchClasses()))
                                .then(() => {
                                  if (activeClass === c.id) {
                                    dispatch(activeClassChanged('unselected'))
                                  }
                                })
                            }
                            open={deleting}
                            setOpen={setDeleting}
                            openText={`delete-${c.id}`}
                            title="Confirm Deletion"
                            text={`Are you sure you want to delete ${c.name}?`}
                            onFailure={(err) =>
                              setErrorMessage(
                                err.message || 'Error Deleting Class',
                              )
                            }
                          />
                        </Box>
                        <Box>{/* Nothing here */}</Box>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Box>
          </div>
        ) : (
          <div>
            <Typography>
              Rearrange classes by dragging & dropping them into the desired
              order.
            </Typography>
            <Box
              sx={{
                mt: 1,
                py: 1,
                px: 2,
                border: `1px solid ${theme.palette.primary.dark}`,
                borderRadius: 3,
                display: 'inline-block',
              }}
            >
              <Typography color="text.secondary" align="center">
                Class Order
              </Typography>
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) {
                    return
                  }
                  setClassOrder(
                    moveItem(
                      classOrder,
                      result.source.index,
                      result.destination?.index,
                    ),
                  )
                }}
              >
                <Droppable droppableId="class-order">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {classOrder.map((step, i) => {
                        let c = classes.find((cl) => cl.id === step)!
                        return (
                          <Draggable
                            key={c.id}
                            draggableId={c.id || i.toString()}
                            index={i}
                          >
                            {(prov) => (
                              <Box
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                              >
                                <Chip
                                  sx={{
                                    my: '4px',
                                    cursor: 'grab',
                                    backgroundColor: 'black',
                                  }}
                                  color="primary"
                                  label={c.name}
                                  avatar={<Avatar>{i + 1}</Avatar>}
                                  variant="outlined"
                                />
                              </Box>
                            )}
                          </Draggable>
                        )
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={
                  activeSection === 'unselected' ||
                  activeCourse === 'unselected'
                }
                onClick={() => {
                  setSaving(true)
                  axios
                    .put('/api/v2/create/class/sequence', {
                      courseID: activeCourse,
                      classes: classOrder,
                    })
                    .then(() => dispatch(fetchClasses()))
                    .then(() => setSaving(false))
                    .then(() => setView('list'))
                    .catch((err) => {
                      setErrorMessage(
                        err?.message || 'Error Submitting Class Order',
                      )
                      dispatch(fetchClasses())
                      setSaving(false)
                    })
                }}
              >
                Save Class Order
              </Button>
              <Button
                variant="outlined"
                startIcon={<BulletedListIcon />}
                disabled={
                  activeSection === 'unselected' ||
                  activeCourse === 'unselected'
                }
                onClick={() => setView('list')}
              >
                Class List
              </Button>
            </Box>
          </div>
        )}
        <Drawer
          anchor="bottom"
          open={formOpen && !saving}
          onClose={compose(setFormOpen, F)}
        >
          <Container maxWidth="sm">
            <Card className="m-4">
              <form className="p-4" onSubmit={form.handleSubmit}>
                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  value={form.values.name}
                  label="Name"
                  name="name"
                  onChange={form.handleChange}
                  error={form.touched.name && !!form.errors.name}
                />

                <TextField
                  fullWidth
                  className="mt-4"
                  variant="standard"
                  value={form.values.description}
                  label="Description"
                  name="description"
                  onChange={form.handleChange}
                  error={form.touched.description && !!form.errors.description}
                />

                <div className="flex flex-row-reverse">
                  <Button
                    variant="contained"
                    color="primary"
                    className="mt-4 mr-3"
                    type="submit"
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </Card>
          </Container>
        </Drawer>
      </Box>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading entities={[classesStatus]} loading={saving} />
    </>
  )
}
